import Immutable from 'seamless-immutable';

import {
  FACET_URL_PARAMS,
  FACET_URL_PATH,
  PAGE_TYPE_CHANGE,
  RECEIVE_SEARCH_RESPONSE,
  REQUEST_SEARCH,
  RESET_FACET_GROUP,
  TOGGLE_FACETS,
  TOGGLE_SELECTED_FACET,
  TOGGLE_SELECTED_FACET_GROUP
} from 'constants/reduxActions';
import { SINGLE_SELECT_FILTERS } from 'constants/appConstants';
import { FILTERS_RE } from 'common/regex';
import marketplace from 'cfg/marketplace.json';

const {
  search: { topBrands }
} = marketplace;

const initialState = {
  requestedUrl: null,
  navigation: [],
  facetUrlQueryParams: null,
  facetUrlPath: null,
  shouldCollapseOnNextResponse: false
};

export function isFacetsVisible(state = false, action) {
  const { type, visible, pageType } = action;
  switch (type) {
    case TOGGLE_FACETS:
      return visible;
    case PAGE_TYPE_CHANGE:
      return pageType === 'search' ? state : false;
    default:
      return state;
  }
}

export const sortTopBrands = (facets, orderedBrands = topBrands) => {
  const facetValuesCopy = [...facets.values];
  const foundTopValues = facetValuesCopy
    .filter((v, i) => {
      const foundIndex = orderedBrands.indexOf(v.name.toLowerCase()) > -1;
      if (foundIndex) {
        facetValuesCopy.splice(i, 1);
        return foundIndex;
      }
    })
    .sort((a, b) => orderedBrands.indexOf(a.name) - orderedBrands.indexOf(b.name));
  return foundTopValues.concat(facetValuesCopy);
};

export default function facets(state = initialState, action) {
  const { type, isFresh, selectedFacetGroupIndex, selectedFacetGroupName, selectedFacetIndex, response, url, facetUrlPath, facetUrlQueryParams } =
    action;
  let newState = Immutable(state);

  switch (type) {
    case TOGGLE_SELECTED_FACET_GROUP: {
      const facetIndex = newState.navigation.findIndex(facet => facet.facetField === selectedFacetGroupName);

      if (facetIndex > -1) {
        newState = newState.updateIn(['navigation', facetIndex, 'isExpanded'], v => !v);
      }

      return newState;
    }

    case TOGGLE_SELECTED_FACET: {
      const facet = newState.navigation[selectedFacetGroupIndex];

      if (facet) {
        if (SINGLE_SELECT_FILTERS[facet.facetField]) {
          // Single-select facets: clear other selections and set the new one
          newState = newState.updateIn(['navigation', selectedFacetGroupIndex, 'values'], values =>
            values.map((value, i) => ({ ...value, selected: i === selectedFacetIndex }))
          );
        } else {
          // Multi-select facets: toggle the selected state
          newState = newState.updateIn(['navigation', selectedFacetGroupIndex, 'values', selectedFacetIndex, 'selected'], v => !v);
        }
      }

      return newState;
    }

    case REQUEST_SEARCH:
      if (isFresh) {
        newState = Immutable(initialState);
      }
      return newState.set('requestedUrl', url);

    case RECEIVE_SEARCH_RESPONSE: {
      const navigationData = response.navigationV2P2 || response.navigationV2;
      if (response.url === state.requestedUrl && navigationData) {
        const shouldCollapse = state.shouldCollapseOnNextResponse;

        // Process navigation flat array structure
        const navigationWithState = navigationData.map(facetGroup => {
          const newFacetGroup = { ...facetGroup };
          const oldFacetGroup = state.navigation?.find(({ facetField }) => facetField === facetGroup.facetField);

          // Preserve UI state OR collapse if flag is set
          newFacetGroup.isExpanded = shouldCollapse ? false : oldFacetGroup ? oldFacetGroup.isExpanded : false;

          // Process facet values
          if (newFacetGroup.values) {
            newFacetGroup.values = newFacetGroup.values.map(facetValue => {
              const newValue = { ...facetValue };

              // Handle facetUrl vs facetZsoUrl
              if (FILTERS_RE.test(facetValue.facetUrl)) {
                newValue.facetZsoUrl = facetValue.facetUrl;
                newValue.facetUrl = null;
              }

              // Add displayName if missing
              if (!facetValue.displayName) {
                newValue.displayName = facetValue.name;
              }

              return newValue;
            });

            // Apply top brands sorting for brand facets
            if (topBrands.length > 0 && facetGroup.facetField === 'brandNameFacet') {
              newFacetGroup.values = sortTopBrands({ values: newFacetGroup.values });
            }
          }

          return newFacetGroup;
        });

        newState = newState.set('navigation', navigationWithState);
        newState = newState.set('shouldCollapseOnNextResponse', false);
      }

      return newState;
    }

    case FACET_URL_PATH:
      newState = newState.set('facetUrlPath', facetUrlPath);
      return newState;

    case FACET_URL_PARAMS:
      newState = newState.set('facetUrlQueryParams', facetUrlQueryParams);
      return newState;

    case RESET_FACET_GROUP:
      // Don't deselect values immediately - let RECEIVE_SEARCH_RESPONSE handle it
      // Just set flag to collapse and clear selections when response arrives
      newState = newState.set('shouldCollapseOnNextResponse', true);

      return newState;

    default:
      return state;
  }
}
